import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../utils/api';

// Server-error alerts → Telegram (owner 2026-08-24). This panel exists so the alerting can be
// dialled back without a deploy: the owner asked for all four categories including 4xx, which is
// the noisy one by nature (most 4xx are the system correctly REFUSING something), so each has its
// own switch here. The recent list is the receipt — it shows what was actually pushed, including
// anything merged or muted by the storm brake, so a quiet chat can be told apart from a broken pipe.

type Kind = { kind: string; enabled: boolean; windowSec: number };
type Row = {
  id: number; sentAt: number; kind: string; module: string; userEmail: string;
  method: string; url: string; status: number; message: string; merged: number; delivered: number;
};

const LABEL: Record<string, { name: string; hint: string }> = {
  http5xx: { name: 'Server errors (HTTP 5xx)', hint: 'Something broke while handling a request — the operator saw a red error.' },
  http4xx: { name: 'Refused requests (HTTP 4xx)', hint: 'Permission denied, bad input, not found. Usually the system working correctly — expect volume.' },
  crash: { name: 'Crashes & uncaught errors', hint: 'Failures outside any request. Previously invisible: they only reached the log file.' },
  background: { name: 'Background tasks', hint: 'Cron jobs, mail sync, OCR queue, auto-invoicing — anything with no operator behind it.' },
};

const KIND_TAG: Record<string, { text: string; bg: string; fg: string }> = {
  http5xx: { text: '5xx', bg: '#FEE2E2', fg: '#991B1B' },
  http4xx: { text: '4xx', bg: '#FEF3C7', fg: '#92400E' },
  crash: { text: 'CRASH', bg: '#FCE7F3', fg: '#9D174D' },
  background: { text: 'JOB', bg: '#FFEDD5', fg: '#9A3412' },
};

const bkk = (t: number) => {
  const d = new Date(t * 1000 + 7 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
};

export default function ErrorAlerts() {
  const [kinds, setKinds] = useState<Kind[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [configured, setConfigured] = useState(true);
  const [maxPerHour, setMaxPerHour] = useState(0);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.adminErrorAlerts()
      .then((d: any) => { setKinds(d.kinds || []); setRows(d.rows || []); setConfigured(!!d.configured); setMaxPerHour(d.maxPerHour || 0); })
      .catch(() => { /* the panel is diagnostics — a failure here must not blank the Admin screen */ })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (kind: string, enabled: boolean) => {
    if (busy) return;
    setBusy(kind);
    setKinds(ks => ks.map(k => (k.kind === kind ? { ...k, enabled } : k)));   // optimistic
    try { const d: any = await api.adminSetErrorAlert(kind, enabled); setKinds(d.kinds || []); }
    catch (e: any) { window.alert(e?.message || 'Failed to save'); load(); }
    finally { setBusy(''); }
  };

  const test = async () => {
    if (busy) return;
    setBusy('test');
    try {
      const d: any = await api.adminTestErrorAlert();
      window.alert(d?.ok ? 'Test alert sent — check the Telegram chat.' : (d?.error || 'Failed to send.'));
      load();
    } catch (e: any) { window.alert(e?.message || 'Failed to send'); }
    finally { setBusy(''); }
  };

  const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', padding: 14, marginBottom: 14 };
  // 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
  // Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like
  // this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular
  // weight, where they were light grey at semibold (600), 10px above and below where it was 8, on one 16px line, on
  // white, over the darker --border-strong line — a 37px band, as every list's is.
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
            <i className="ti ti-brand-telegram" style={{ marginRight: 6 }} />Error alerts → Telegram
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={test} disabled={!!busy || !configured}
            style={{ padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: busy || !configured ? 'not-allowed' : 'pointer', opacity: busy || !configured ? 0.6 : 1 }}>
            {busy === 'test' ? <><i className="ti ti-loader-2" style={{ display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 5 }} />Sending…</> : <><i className="ti ti-send" style={{ marginRight: 5 }} />Send test alert</>}
          </button>
          <button onClick={load} disabled={loading} style={{ padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            <i className="ti ti-refresh" style={{ marginRight: 5 }} />Refresh
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>
          Every alert carries the time (Bangkok), the operator's account, the module and the error text.
          The same error is sent once per window and later repeats are merged into the next message; more than {maxPerHour} distinct
          errors in an hour mutes the rest of that hour, which is still recorded below.
        </div>
        {!configured && (
          <div style={{ fontSize: 12.5, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
            <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />
            Not configured — ALERT_BOT_TOKEN / ALERT_CHAT_ID are missing from the server .env. Nothing is being sent.
          </div>
        )}
        {kinds.map(k => (
          <label key={k.kind} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
            <input type="checkbox" checked={k.enabled} disabled={busy === k.kind} onChange={e => toggle(k.kind, e.target.checked)} style={{ marginTop: 2, cursor: 'pointer' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                {LABEL[k.kind]?.name || k.kind}
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)', marginLeft: 8 }}>
                  repeats merged for {Math.round(k.windowSec / 60)} min
                </span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{LABEL[k.kind]?.hint}</div>
            </div>
          </label>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', padding: '12px 14px' }}>
          Recent alerts <span style={{ fontWeight: 500, color: 'var(--text-tertiary)' }}>({rows.length})</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Time</th><th style={th}>Type</th><th style={th}>Module</th>
              <th style={th}>Operator</th><th style={th}>Error</th><th style={th}>Sent</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const tag = KIND_TAG[r.kind] || { text: r.kind, bg: 'var(--c-gray-100)', fg: 'var(--text-secondary)' };
                return (
                  <tr key={r.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{bkk(r.sentAt)}</td>
                    <td style={td}><span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: tag.bg, color: tag.fg }}>{tag.text}</span></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.module}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{r.userEmail || '—'}</td>
                    <td style={{ ...td, maxWidth: 460 }}>
                      <div>{r.message}</div>
                      {!!r.url && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.method} {r.url}{r.status ? ` · ${r.status}` : ''}</div>}
                      {r.merged > 0 && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>↺ {r.merged} more merged in</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {r.delivered ? <span style={{ color: '#15803D' }}>✓</span> : <span style={{ color: 'var(--text-tertiary)' }} title="Recorded but not pushed — muted by the hourly cap, or Telegram rejected it">muted</span>}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && !loading && (
                <tr><td style={{ ...td, color: 'var(--text-tertiary)' }} colSpan={6}>No alerts yet — nothing has gone wrong since this was switched on.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
